import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FiArrowLeft,
  FiEdit,
  FiTrash2,
  FiMail,
  FiPhone,
  FiUser,
  FiCalendar,
  FiClock,
  FiShield,
  FiCheckCircle,
  FiXCircle,
} from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import teamService from '../../services/teamService';
import { TeamMember } from '../../types/team.types';
import Button from '../../components/common/Button/Button';
import Card from '../../components/common/Card/Card';
import Loader from '../../components/common/Loader/Loader';
import { formatToIST, getRelativeTime } from '../../utils/dateHelper';
import styles from './ViewTeam.module.css';

const ViewTeam: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [member, setMember] = useState<TeamMember | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      fetchTeamMember(id);
    } else {
      toast.error('Invalid team member ID');
      navigate('/team');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchTeamMember = async (memberId: string) => {
    try {
      setLoading(true);
      console.log('📖 Fetching team member with ID:', memberId);

      const data = await teamService.getTeamMemberById(memberId);
      console.log('✅ Team member loaded:', data.name);

      setMember(data);
    } catch (error: any) {
      console.error('❌ Failed to fetch team member:', error);
      toast.error(error.response?.data?.message || 'Team member not found');
      navigate('/team');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!member) return;

    if (member.role === 'superadmin') {
      toast.error('Cannot delete SuperAdmin account');
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to delete "${member.name}"?\n\nThis action cannot be undone.`
    );

    if (!confirmed) return;

    try {
      console.log('🗑️ Deleting team member with ID:', member.id);
      await teamService.deleteTeamMember(member.id);
      toast.success('Team member deleted successfully');
      navigate('/team');
    } catch (error: any) {
      console.error('❌ Delete failed:', error);
      toast.error(error.response?.data?.message || 'Failed to delete team member');
    }
  };

  const getRoleBadgeColor = (role: string) => {
    const colors: Record<string, string> = {
      superadmin: '#DC2626',
      admin: '#F59E0B',
      developer: '#8B5CF6',
      editor: '#3B82F6',
      manager: '#10B981',
      executive: '#06B6D4',
      blogger: '#EC4899',
      affiliater: '#6366F1',
    };
    return colors[role] || '#64748b';
  };

  const getRoleLabel = (role: string) => {
    const labels: Record<string, string> = {
      superadmin: 'Super Admin',
      admin: 'Admin',
      developer: 'Developer',
      editor: 'Editor',
      manager: 'Manager',
      executive: 'Executive',
      blogger: 'Blogger',
      affiliater: 'Affiliater',
    };
    return labels[role] || role;
  };

  if (loading) {
    return (
      <div className={styles.loaderContainer}>
        <Loader size="large" text="Loading team member..." />
      </div>
    );
  }

  if (!member) {
    return (
      <div className={styles.loaderContainer}>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ color: 'var(--text-primary)', marginBottom: 'var(--spacing-lg)' }}>
            Team Member Not Found
          </h2>
          <p style={{ color: 'var(--text-secondary)', marginBottom: 'var(--spacing-xl)' }}>
            The team member you're looking for doesn't exist or has been removed.
          </p>
          <Button variant="primary" onClick={() => navigate('/team')}>
            Back to Team
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.viewTeam}>
      {/* Header Actions */}
      <div className={styles.headerActions}>
        <Button variant="ghost" icon={<FiArrowLeft />} onClick={() => navigate('/team')}>
          Back to Team
        </Button>

        <div className={styles.actions}>
          {(user?.role === 'superadmin' || user?.role === 'admin') && (
            <Button
              variant="primary"
              icon={<FiEdit />}
              onClick={() => navigate(`/team/edit/${member.id}`)}
            >
              Edit Member
            </Button>
          )}
          {user?.role === 'superadmin' && member.role !== 'superadmin' && (
            <Button variant="danger" icon={<FiTrash2 />} onClick={handleDelete}>
              Delete
            </Button>
          )}
        </div>
      </div>

      {/* Profile Header */}
      <Card className={styles.profileCard}>
        <div className={styles.profileHeader}>
          <img
            src={
              member.profilePhoto?.url ||
              `https://ui-avatars.com/api/?name=${encodeURIComponent(member.name)}&background=3B82F6&color=fff&size=256`
            }
            alt={member.name}
            className={styles.profileImage}
            onError={(e) => {
              (e.target as HTMLImageElement).src =
                `https://ui-avatars.com/api/?name=${encodeURIComponent(member.name)}&background=3B82F6&color=fff&size=256`;
            }}
          />
          <div className={styles.profileInfo}>
            <h1 className={styles.profileName}>{member.name}</h1>
            <p className={styles.profileEmail}>
              <FiMail /> {member.email}
            </p>
            <div className={styles.profileBadges}>
              <span
                className={styles.roleBadge}
                style={{ backgroundColor: getRoleBadgeColor(member.role) }}
              >
                <FiShield /> {getRoleLabel(member.role)}
              </span>
              <span
                className={`${styles.statusBadge} ${
                  member.isActive ? styles.statusActive : styles.statusInactive
                }`}
              >
                {member.isActive ? (
                  <>
                    <FiCheckCircle /> Active
                  </>
                ) : (
                  <>
                    <FiXCircle /> Inactive
                  </>
                )}
              </span>
            </div>
          </div>
        </div>
      </Card>

      {/* Contact Information */}
      <Card title="📞 Contact Information" className={styles.section}>
        <div className={styles.infoGrid}>
          <div className={styles.infoItem}>
            <div className={styles.infoIcon}>
              <FiMail />
            </div>
            <div className={styles.infoContent}>
              <p className={styles.infoLabel}>Email Address</p>
              <p className={styles.infoValue}>{member.email}</p>
              {member.isEmailVerified && (
                <span className={styles.verifiedBadge}>✓ Verified</span>
              )}
            </div>
          </div>

          <div className={styles.infoItem}>
            <div className={styles.infoIcon}>
              <FiPhone />
            </div>
            <div className={styles.infoContent}>
              <p className={styles.infoLabel}>Mobile Number</p>
              <p className={styles.infoValue}>{member.mobile}</p>
              {member.isMobileVerified && (
                <span className={styles.verifiedBadge}>✓ Verified</span>
              )}
            </div>
          </div>

          <div className={styles.infoItem}>
            <div className={styles.infoIcon}>
              <FiUser />
            </div>
            <div className={styles.infoContent}>
              <p className={styles.infoLabel}>User ID</p>
              <p className={styles.infoValue}>{member.userID}</p>
            </div>
          </div>
        </div>
      </Card>

      {/* Account Details */}
      <Card title="🔐 Account Details" className={styles.section}>
        <div className={styles.infoGrid}>
          <div className={styles.infoItem}>
            <div className={styles.infoIcon}>
              <FiCalendar />
            </div>
            <div className={styles.infoContent}>
              <p className={styles.infoLabel}>Created At</p>
              <p className={styles.infoValue}>{formatToIST(member.createdAt, 'PPpp')} IST</p>
              <p className={styles.infoTime}>{getRelativeTime(member.createdAt)}</p>
            </div>
          </div>

          <div className={styles.infoItem}>
            <div className={styles.infoIcon}>
              <FiClock />
            </div>
            <div className={styles.infoContent}>
              <p className={styles.infoLabel}>Last Updated</p>
              <p className={styles.infoValue}>{formatToIST(member.updatedAt, 'PPpp')} IST</p>
              <p className={styles.infoTime}>{getRelativeTime(member.updatedAt)}</p>
            </div>
          </div>

          <div className={styles.infoItem}>
            <div className={styles.infoIcon}>
              <FiClock />
            </div>
            <div className={styles.infoContent}>
              <p className={styles.infoLabel}>Last Login</p>
              {member.lastLogin ? (
                <>
                  <p className={styles.infoValue}>{formatToIST(member.lastLogin, 'PPpp')} IST</p>
                  <p className={styles.infoTime}>{getRelativeTime(member.lastLogin)}</p>
                </>
              ) : (
                <p className={styles.noActivity}>Never logged in</p>
              )}
            </div>
          </div>

          {member.createdBy && (
            <div className={styles.infoItem}>
              <div className={styles.infoIcon}>
                <FiUser />
              </div>
              <div className={styles.infoContent}>
                <p className={styles.infoLabel}>Created By</p>
                <p className={styles.infoValue}>{member.createdBy.name}</p>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Security Info (SuperAdmin only) */}
      {user?.role === 'superadmin' && (
        <Card title="🛡️ Security Information" className={styles.section}>
          <div className={styles.infoGrid}>
            <div className={styles.infoItem}>
              <div className={styles.infoContent}>
                <p className={styles.infoLabel}>Login Attempts</p>
                <p className={styles.infoValue}>{member.loginAttempts || 0}</p>
              </div>
            </div>

            <div className={styles.infoItem}>
              <div className={styles.infoContent}>
                <p className={styles.infoLabel}>Account ID</p>
                <p className={styles.techData}>{member.id}</p>
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default ViewTeam;